import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
// import { ReferenceImageUpload } from "@/components/ui/reference-image-upload";
import {
  LogoPlacementSelector,
  LogoPlacement,
} from "@/components/ui/logo-placement-selector";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { Brand } from "@/types/brand";
import { cn } from "@/lib/utils";
import { ImageVisualStyleOptions } from "@/types/brand";
import { Badge } from "@/components/ui/badge";

// ImageStyle interface to match backend structure
interface ImageStyle {
  visualStyles?: ImageVisualStyleOptions[] | undefined;
  referenceImage?: string;
  embedLogo?: boolean;
  logoPlacement?: string;
}

const formSchema = z.object({
  visualStyles: z
    .array(z.nativeEnum(ImageVisualStyleOptions))
    .min(1, "Please select at least one visual style"),
  referenceImage: z.string().optional(),
  embedLogo: z.boolean(),
  logoPlacement: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface VisualStyleFormProps {
  data: Brand;
  onSubmit: (data: { imageStyle: ImageStyle }) => void;
  mode: "onboarding" | "edit";
  onBack?: () => void;
  logoUrl?: string;
}

interface StyleOptionProps {
  value: ImageVisualStyleOptions;
  title: string;
  description: string;
  imageSrc: string;
  selected: boolean;
  onToggle: (value: ImageVisualStyleOptions) => void;
}

function CompactStyleOption({
  value,
  title,
  description,
  imageSrc,
  selected,
  onToggle,
}: StyleOptionProps) {
  return (
    <div
      className={cn(
        "border rounded-lg overflow-hidden cursor-pointer transition-all hover:shadow-md relative w-[calc(50%-6px)] sm:w-[calc(33.333%-8px)] lg:w-[calc(25%-9px)]",
        selected
          ? "ring-2 ring-primary border-primary"
          : "hover:border-muted-foreground",
      )}
      onClick={() => onToggle(value)}
    >
      {/* Smaller image */}
      <div className="aspect-square bg-muted relative overflow-hidden">
        <img
          src={imageSrc}
          alt={title}
          className="w-full h-full object-cover"
        />
        {/* Selection indicator */}
        {selected && (
          <div className="absolute inset-0 bg-primary/10 flex items-center justify-center">
            <div className="bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center">
              ✓
            </div>
          </div>
        )}
      </div>

      {/* Compact text */}
      <div className="p-3">
        <h3 className="font-medium text-sm">{title}</h3>
        <p className="text-xs text-muted-foreground line-clamp-2">
          {description}
        </p>
      </div>
    </div>
  );
}

export function VisualStyleForm({
  data,
  onSubmit,
  mode,
  onBack,
  logoUrl,
}: VisualStyleFormProps) {
  // Parse imageStyle from brand data
  let imageStyle: ImageStyle = {};
  if (data.imageStyle && typeof data.imageStyle === "object") {
    imageStyle = data.imageStyle as ImageStyle;
  }

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      visualStyles: imageStyle.visualStyles || [],
      referenceImage: imageStyle.referenceImage || "",
      embedLogo: imageStyle.embedLogo || false,
      logoPlacement: imageStyle.logoPlacement || "top-right",
    },
  });

  const handleFormSubmit = (values: FormValues) => {
    const imageStyleData: ImageStyle = {
      visualStyles: values.visualStyles,
      referenceImage: values.referenceImage || undefined,
      embedLogo: values.embedLogo,
      logoPlacement: values.logoPlacement,
    };

    onSubmit({
      imageStyle: imageStyleData,
    });
  };

  const styleOptions: {
    value: ImageVisualStyleOptions;
    title: string;
    description: string;
    imageSrc: string;
  }[] = [
    {
      value: ImageVisualStyleOptions.Photorealistic,
      title: "Photorealistic",
      description:
        "Lifelike imagery resembling a real-world photo. Great for credibility, especially with human/animal subjects.",
      imageSrc:
        "https://images.creator.li/cdn-cgi/image/width=200,height=200,quality=80,format=webp,fit=cover/app/visual-styles/photorealistic.png",
    },
    {
      value: ImageVisualStyleOptions.FlatIllustration,
      title: "Flat Illustration",
      description:
        "Clean, 2D illustrations with minimal depth. Works well for conceptual sections, icons, or explainer visuals.",
      imageSrc:
        "https://images.creator.li/cdn-cgi/image/width=200,height=200,quality=80,format=webp,fit=cover/app/visual-styles/flat-illustration.png",
    },
    {
      value: ImageVisualStyleOptions.ThreedRendered,
      title: "3D Rendered",
      description:
        "Stylized, 3D visuals with depth and texture. Eye-catching and modern. Ideal for tech or product posts.",
      imageSrc:
        "https://images.creator.li/cdn-cgi/image/width=200,height=200,quality=80,format=webp,fit=cover/app/visual-styles/3d-rendered.png",
    },
    {
      value: ImageVisualStyleOptions.Abstract,
      title: "Abstract",
      description:
        "Shapes, gradients, and clean lines with little to no illustration. Good for a sleek, brand-focused look.",
      imageSrc:
        "https://images.creator.li/cdn-cgi/image/width=200,height=200,quality=80,format=webp,fit=cover/app/visual-styles/minimal-abstract.png",
    },
    {
      value: ImageVisualStyleOptions.VectorArt,
      title: "Vector Art",
      description:
        "Soft brushstroke-style art. Can make wellness, lifestyle, and nature topics feel warm and inviting.",
      imageSrc:
        "https://images.creator.li/cdn-cgi/image/width=200,height=200,quality=80,format=webp,fit=cover/app/visual-styles/vector-art.png",
    },
    {
      value: ImageVisualStyleOptions.HandDrawnSketch,
      title: "Hand-drawn sketch",
      description:
        "Rough, artistic style mimicking pen or pencil. Good for creative or founder-led brand storytelling.",
      imageSrc:
        "https://images.creator.li/cdn-cgi/image/width=200,height=200,quality=80,format=webp,fit=cover/app/visual-styles/handdrawn-sketch.png",
    },
    {
      value: ImageVisualStyleOptions.Watercolor,
      title: "Watercolor",
      description:
        "Soft brushstroke-style art. Can make wellness, lifestyle, and nature topics feel warm and inviting.",
      imageSrc:
        "https://images.creator.li/cdn-cgi/image/width=200,height=200,quality=80,format=webp,fit=cover/app/visual-styles/watercolour.png",
    },
    {
      value: ImageVisualStyleOptions.CutoutCollage,
      title: "Cutout collage",
      description:
        "A blend of photos and textures layered like a scrapbook. Trendy and playful.",
      imageSrc:
        "https://images.creator.li/cdn-cgi/image/width=200,height=200,quality=80,format=webp,fit=cover/app/visual-styles/cutout-collage.png",
    },
  ];

  const selectedStyles = form.watch("visualStyles");

  const handleStyleToggle = (value: ImageVisualStyleOptions) => {
    const currentStyles = form.getValues("visualStyles");
    if (currentStyles.includes(value)) {
      // Remove the style
      form.setValue(
        "visualStyles",
        currentStyles.filter((style) => style !== value),
      );
    } else {
      // Add the style
      form.setValue("visualStyles", [...currentStyles, value]);
    }
  };

  const renderTitle = () => {
    if (mode === "onboarding") {
      return (
        <>
          <CardTitle>Visual styles</CardTitle>
          <CardDescription>
            Choose the various image styles for the cover image.
          </CardDescription>
        </>
      );
    }
    return null;
  };

  const renderButtons = () => {
    if (mode === "onboarding") {
      return (
        <div className="flex justify-between pt-6">
          {onBack && (
            <Button type="button" variant="outline" onClick={onBack}>
              Back
            </Button>
          )}
          <Button type="submit">Continue</Button>
        </div>
      );
    }

    return (
      <div className="flex justify-end pt-6">
        <Button type="submit" className="bg-black hover:bg-black/90">
          Save
        </Button>
      </div>
    );
  };

  const formContent = (
    <form onSubmit={form.handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* Visual Styles Section */}
      <div>
        <div className="mb-4">
          <h3 className="text-lg font-medium mb-2">Choose Visual Styles</h3>
          <p className="text-sm text-muted-foreground">
            Select one or more visual styles that represent your brand. We'll
            randomize the style when generating the cover image.
          </p>
          {selectedStyles.length > 0 && (
            <div className="mt-3 p-2 bg-primary/5 border border-primary/20 rounded-lg">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs font-medium text-primary">
                  Selected ({selectedStyles.length}):
                </span>
              </div>
              <div className="flex flex-wrap gap-1">
                {selectedStyles.map((style) => (
                  <Badge
                    key={style}
                    variant="default"
                    className="bg-primary text-primary-foreground hover:bg-primary/90 text-xs py-0.5 px-2"
                  >
                    ✓ {styleOptions.find((opt) => opt.value === style)?.title}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-3">
          {styleOptions.map((style) => (
            <CompactStyleOption
              key={style.value}
              value={style.value}
              title={style.title}
              description={style.description}
              imageSrc={style.imageSrc}
              selected={selectedStyles.includes(style.value)}
              onToggle={handleStyleToggle}
            />
          ))}
        </div>

        {form.formState.errors.visualStyles && (
          <p className="text-sm text-destructive mt-2">
            {form.formState.errors.visualStyles.message}
          </p>
        )}
      </div>

      {/* Logo Embedding Section */}
      <div>
        <div className="mb-4">
          <h3 className="text-lg font-medium mb-2">Logo Settings</h3>
          <p className="text-sm text-muted-foreground">
            Choose whether to embed your logo on generated images.
          </p>
        </div>

        <div className="flex items-center space-x-2">
          <Switch
            id="embed-logo"
            checked={form.watch("embedLogo")}
            onCheckedChange={(checked) => form.setValue("embedLogo", checked)}
          />
          <Label htmlFor="embed-logo" className="text-sm font-medium">
            Embed logo on images
          </Label>
        </div>

        {form.watch("embedLogo") && (
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-700">
              Your brand logo will be automatically placed on all generated
              images according to your placement settings below.
            </p>
          </div>
        )}
      </div>

      {/* Logo Placement Section */}
      {form.watch("embedLogo") && (
        <div className="transition-all duration-300 ease-in-out">
          <div className="mb-4">
            <h3 className="text-lg font-medium mb-2">Select Logo Placement</h3>
            <p className="text-sm text-muted-foreground">
              Choose where your logo should be positioned on your creative
              content.
            </p>
          </div>

          <LogoPlacementSelector
            value={form.watch("logoPlacement") as LogoPlacement}
            onChange={(value) => form.setValue("logoPlacement", value)}
            logoUrl={logoUrl}
            referenceImageUrl={form.watch("referenceImage")}
          />
        </div>
      )}

      {/* Reference Image Section */}
      {/*<div>
        <div className="mb-4">
          <h3 className="text-lg font-medium mb-2">
            Add a reference image (Optional)
          </h3>
          <p className="text-sm text-muted-foreground">
            Upload a reference image to guide the visual style and aesthetic of
            your content.
          </p>
        </div>

        <ReferenceImageUpload
          value={form.watch("referenceImage")}
          onChange={(value) => form.setValue("referenceImage", value)}
        />
      </div>*/}

      {renderButtons()}
    </form>
  );

  if (mode === "onboarding") {
    return (
      <Card className="w-full max-w-4xl mx-auto">
        <CardHeader>{renderTitle()}</CardHeader>
        <CardContent>{formContent}</CardContent>
      </Card>
    );
  }

  return formContent;
}
